import { z } from 'zod';

/**
 * Item aggregation direction constants (string literals).
 */
export const AGGREGATION_DIRECTION = {
  DESCENDANT: 'ITEM_AGGREGATION_DIRECTION_DESCENDANT',
  ANCESTOR: 'ITEM_AGGREGATION_DIRECTION_ANCESTOR',
  SELF_OR_DESCENDANT: 'ITEM_AGGREGATION_DIRECTION_SELF_OR_DESCENDANT',
  SELF_AND_DESCENDANT: 'ITEM_AGGREGATION_DIRECTION_SELF_AND_DESCENDANT',
} as const;

/**
 * Item aggregation field constants (string literals).
 */
export const AGGREGATION_FIELD = {
  TIME_SPENT: 'ITEM_AGGREGATION_FIELD_TIME_SPENT',
  LOW_EFFORT: 'ITEM_AGGREGATION_FIELD_LOW_EFFORT',
  HIGH_EFFORT: 'ITEM_AGGREGATION_FIELD_HIGH_EFFORT',
  EXPECTED_FINISH_DATE: 'ITEM_AGGREGATION_FIELD_EXPECTED_FINISH_DATE',
} as const;

/**
 * Item aggregation function name constants (string literals).
 */
export const AGGREGATION_FUNCTION = {
  SUM: 'ITEM_AGGREGATION_FUNCTION_NAME_SUM',
  MIN: 'ITEM_AGGREGATION_FUNCTION_NAME_MIN',
  MAX: 'ITEM_AGGREGATION_FUNCTION_NAME_MAX',
  AVG: 'ITEM_AGGREGATION_FUNCTION_NAME_AVG',
} as const;

/**
 * Zod schema for aggregation direction enum.
 */
export const aggregationDirectionSchema = z.enum(AGGREGATION_DIRECTION);

/**
 * Zod schema for aggregation field enum.
 */
export const aggregationFieldSchema = z.enum(AGGREGATION_FIELD);

/**
 * Zod schema for aggregation function enum.
 */
export const aggregationFunctionSchema = z.enum(AGGREGATION_FUNCTION);

/**
 * Zod schema for item aggregation specification.
 *
 * Defines what aggregation to calculate (function, field, direction).
 */
export const itemAggregationSpecSchema = z.object({
  function: aggregationFunctionSchema.optional(),
  field: aggregationFieldSchema.optional(),
  direction: aggregationDirectionSchema.optional(),
});

/**
 * Zod schema for item aggregation value.
 *
 * Discriminated union representing different value types.
 * Uses union without explicit 'type' field to match existing structure.
 */
export const itemAggregationValueSchema = z.union([
  z.object({ numeric: z.number() }),
  z.object({ date: z.string() }),
  z.object({ text: z.string() }),
  z.object({ null: z.literal(true) }),
]);

/**
 * Zod schema for item aggregation result.
 *
 * Contains the specification and the calculated result.
 */
export const itemAggregationResultSchema = z.object({
  spec: itemAggregationSpecSchema,
  result: itemAggregationValueSchema,
});

/**
 * Zod schema for item aggregations results.
 *
 * Contains all aggregation results for a single item.
 */
export const itemAggregationsResultsSchema = z.object({
  id: z.number().int().positive(),
  aggregations: z.array(itemAggregationResultSchema),
});

export type AggregationDirection = z.infer<typeof aggregationDirectionSchema>;
export type AggregationField = z.infer<typeof aggregationFieldSchema>;
export type AggregationFunction = z.infer<typeof aggregationFunctionSchema>;
export type ItemAggregationSpecDTO = z.infer<typeof itemAggregationSpecSchema>;
export type ItemAggregationValueDTO = z.infer<typeof itemAggregationValueSchema>;
export type ItemAggregationResultDTO = z.infer<typeof itemAggregationResultSchema>;
export type ItemAggregationsResultsDTO = z.infer<typeof itemAggregationsResultsSchema>;
